import { resetChest } from './chest';
import { clearLock } from './lockon';
import { BOSS_ROSTER, FINAL_BOSS_ID } from '../data/boss-roster';
import { clearBossField } from '../render/boss-field';
import { setFieldMul } from './spawn';
import { clearSchedule } from './fx';
import { setBoss } from '../entities/enemies';
import { setAccum } from '../game';
import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { ETYPE, POOLS, enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { CAM_OFF, camera, scene } from '../render/scene';
import { STATE, dropChest, dropOrb, removeEnemy, shake } from './fx';
import { RS, diff, spawnAtRing, spawnEnemy, updateSpawner } from './spawn';
import { banner, gameOver, showPrompt } from '../ui/hud';
import { ARENA, STAGES, applyTheme, buildTerrain, safeSpot } from '../world/arena';
import { P, beamPivot, slashFx, vortexRig } from '../world/player';
import { bullets, chests, debris, ebullets, mines, missiles, orbs, pedestal, portal, rings, shocks, strikes } from '../world/pools';
import { eliteDue, resetElites, scheduleNextElite, spawnEliteSquad } from './elites';
import { resetFormations } from './formations';
import { resetWaves, updateWaves } from './waves';
import { resetHordes, updateHorde } from './horde';
import { camFocus, resetCamera } from './camera';
/* ============================================================
   關卡流程 —— 單一連續場，20 分鐘，時鐘就是難度條
   ============================================================

   M7 之前是「四個階段，每階段計時 42–85 秒後一隻 Boss，打完走傳送門」。
   轉向倖存者類之後整個換掉（`01-GDD.md` §3.1）：

     0:00 ─────────────────────────────────── 20:00 ──▶
      │ 外圍機庫 │ 熔煉層 │ 冷卻塔 │  核心  │
      │        ▼       ▼       ▼    ▼      ▼
      │      Boss1   Boss2   Boss3 Boss4  最終 Boss
      5:00     9:00   13:00 16:00  20:00   （打倒牠＝通關）

   - **沒有房間、沒有出口選擇、沒有補給台。** 成長全部來自升級與寶箱
   - 每 5 分鐘**換幕**：配色與地形主題切換（沿用四張場地的既有資產）
   - 前四隻 Boss **定時降臨**。打贏繼續打，打輸就死 ——
     牠們不是章節終點，是途中的大麻煩
   - **20:00 降臨的最終 Boss（KAISER）被打倒＝通關**
     （2026-08-06 使用者拍板；在那之前是「活到 20 分鐘」就贏）。
     所以 20:00 不是終點而是最後一場考試的開始，時鐘照走

   `STATE.stage` 保留，但意思變了：它現在是「第幾幕」（0–3），
   純粹決定配色、地形主題與該出哪隻 Boss，不再是進度。 */

export var RUN_LEN = 20 * 60;              // 一場的長度（秒）
export var ACT_LEN = RUN_LEN / 4;          // 每一幕 5 分鐘
/* Boss 的降臨時刻**由時刻表決定**（`data/boss-roster.ts`）——
   那張表同時是圖鑑玩家模式的過濾條件，兩邊共用一份才不會有
   「圖鑑列了但打不到」或反過來的落差。

   最後一隻**就排在 20:00**（2026-08-06）。這在舊規則下是不行的 ——
   當時 `STATE.t >= RUN_LEN` 會在同一幀判勝利，玩家連看都看不到牠
   （headless 測試只數「spawn 過幾隻」所以照樣綠燈，是開瀏覽器才發現的）。
   現在勝利判定改成「打倒最終 Boss」，那個衝突不存在了。 */
export var BOSS_AT = BOSS_ROSTER.map(function (b) { return b.at; });

export function clearEntities(keepPlayer?) {
  clearSchedule();
  clearLock();
  for (var i = enemies.length - 1; i >= 0; i--) removeEnemy(enemies[i]);
  [bullets, ebullets, missiles, mines, strikes, shocks, debris, rings, orbs, chests].forEach(function (p) {
    p.items.forEach(function (o) { if (o.alive) { o.alive = false; scene.remove(o.m); } });
  });
  /* 補給台與傳送門在 M7 退場（成長改成全部來自升級與寶箱）。
     物件本身還留在 world/pools.ts，這裡只確保它們永遠不顯示 ——
     M8 的寶箱會接手 pedestal 的視覺。 */
  portal.g.visible = false; portal.active = false;
  pedestal.g.visible = false; pedestal.active = false;
  beamPivot.visible = false; vortexRig.visible = false; slashFx.visible = false;
  P.ultT = -1;
}

/** 開場。整場只呼叫一次 —— 沒有「下一個階段」了。 */
export function startRunFlow() {
  STATE.stage = 0;
  STATE.phase = 'wave';
  STATE.t = 0; STATE.spawnT = 1.0;
  RS.act = 0; RS.nextBoss = 0;
  resetElites();
  resetFormations();
  resetWaves();
  resetHordes();
  resetChest();
  resetCamera();
  clearEntities();
  clearBossField();
  setFieldMul(1);
  RNG.seed(STATE.seed);
  enterAct(0, true);
}


/** 換幕：只換配色與地形，不重置玩家、不清場、不中斷戰鬥。 */
function enterAct(i, initial?) {
  RS.act = i;
  STATE.stage = i;
  var S = STAGES[i];
  applyTheme(S);
  /* 地形用「幕序號」當種子偏移而不是 RNG 當下的狀態：
     換幕發生在戰鬥中，若吃當下的 RNG 會讓「這一幕的地形長怎樣」
     取決於玩家前面殺了幾隻（RNG 被抽了幾次），同種子重玩就不一樣了。 */
  var keep = RNG.s;
  RNG.seed(STATE.seed + i * 7919 + 13);
  buildTerrain(S, i);
  RNG.s = keep;

  if (initial) {
    P.pos.set(0, 0, 0); P.vel.set(0, 0, 0);
    P.prevX = 0; P.prevZ = 0; P.viewX = 0; P.viewZ = 0; setAccum(0);
    /* 飛行狀態同樣要歸零 —— 上一場結束時人在空中的話，同種子第二次開局
       會從半空中起跑（同上面那條註解的坑，只是換一個軸）。 */
    P.y = 0; P.vy = 0; P.prevY = 0; P.viewY = 0; P.apexY = 0;
    P.flying = false; P.thrust = 0; P.thrustCd = 0; P.airT = 0;   // 開場空條，見 world/player.ts
    /* 朝向、步態相位、衝刺狀態全部歸零。少了這幾行，同一顆種子第二次開局會從
       上一場結束的角度起跑 —— 主砲鎖定與飛彈散佈跟著偏移，整場結果完全不同。 */
    /* 開局朝向是 **π（朝 -z，畫面深處）** 而不是 0。使用者 2026-08-06：
       「出擊是面向前面，不是面對我方」—— 戰場鏡頭在玩家的 +z 側，
       朝向 0 等於機體一生成就正面對著鏡頭站著，讀起來像在等人而不是出擊。
       ⚠️ 這個值進狀態快照，改它會動到黃金雜湊（`src/sim.test.ts`）。 */
    P.faceMove = Math.PI; P.faceAim = Math.PI; P.walkPhase = 0; P.swing = 0;
    /* 近戰的連段與旋刃成長也要歸零。**同型的坑這個檔已經記過兩次**
       （朝向、飛行狀態），連段是第三次：不清的話同一顆種子第二次開局會從
       「上一場最後停在第幾段」起跑，第一刀的傷害就不一樣，整場跟著岔開。
       決定性測試（`src/lancer.test.ts`）就是踩到這個才紅的。 */
    P.combo = 0; P.comboT = 0; P.vortexSpin = 0; P.vortexBeat = 0;
    P.sh = P.shMax; P.iframe = 1.2; P.thrust = P.s.thrustMax || 100;
    P.midCd = 0; P.ultCd = 0; P.fireCd = 0;
    camera.position.set(CAM_OFF.x, CAM_OFF.y, CAM_OFF.z);
    banner('作戰開始 · ' + S.name);
  } else {
    banner('區域轉移 · ' + S.name);
    SFX.portal();
  }
}

export function startBoss(idx?) {
  var i = idx === undefined ? RS.act : idx;
  /* 名單來自時刻表；`idx` 超出範圍時退回這一幕的預設（主控台的
     `boss` 指令會用到，它是照當下的幕叫的）。 */
  var id = (BOSS_ROSTER[i] && BOSS_ROSTER[i].id) || STAGES[Math.min(3, i)].boss;
  var a = rr(0, TAU), d = 26;
  var T = ETYPE[id];
  /* 降臨距離要**吃體型**（大隻的要退遠一點才不會生在玩家臉上），
     但**不能太遠** —— 26 + r 大約落在畫面邊緣，玩家一轉頭就看得到牠降臨。
     第一版用 34 + r×2（KAISER 是 44），實看截圖裡整台在畫面外，
     而牠的移速追不回來，等於「聽到警報但永遠看不到東西」。 */
  var sp = safeSpot(P.pos.x + Math.cos(a) * (d + T.r), P.pos.z + Math.sin(a) * (d + T.r), 5);
  var e = spawnEnemy(id, sp.x, sp.z);
  if (e) setBoss(e);
  /* 場面稀釋（設計文件：稀釋不是清空）。係數是這一隻自己的 `dilute`。 */
  setFieldMul(T.dilute || 0.3);
  banner('警告 · ' + T.bn);
  /* 進場運鏡：鏡頭先飄去看它一眼再回來（1.5 秒）。
     Boss 降臨是一場裡最貴的事件，玩家要**先看到它在哪**才有得反應。 */
  camFocus(sp.x, sp.z, 1.5);
  SFX.alarm(); shake(0.7);
}

/** Boss 死亡。**不再中斷流程** —— 打贏繼續打，時鐘照走。

    ⚠️ 例外是**最終 Boss**（`FINAL_BOSS_ID`，20:00 降臨）：打倒牠＝通關
    （2026-08-06 使用者拍板）。在那之前通關條件是「撐到 20 分鐘」。 */
export function onBossKilled(src) {
  /* 最終 Boss：**先判、先結束**，不要走下面那段獎勵 ——
     通關的當下再掉一個金箱與一圈經驗球是沒有意義的（畫面已經切到結算），
     而且那些掉落物會留在場上被下一場的 `resetRun` 收掉，白花一次配置。 */
  if (src && src.type === FINAL_BOSS_ID) {
    banner('最終目標擊破 · 作戰完成');
    SFX.boom(); shake(1);
    setFieldMul(1);
    gameOver(true);
    return;
  }
  banner('目標擊破 · 繼續前進');
  SFX.boom(); shake(0.8);
  /* 稀釋解除。**放在這裡而不是每幀檢查 `boss`** —— 每幀檢查的話
     Boss 死亡與生成器的下一次 tick 之間會有一段係數對不上的空窗。 */
  setFieldMul(1);
  var bx = src ? src.pos.x : P.pos.x, bz = src ? src.pos.z : P.pos.z;
  /* **金箱**（2026-08-04 晚，使用者拍板）。在這之前是 `P.bossUp++`，
     介面當場彈出機體武裝強化三選一。改成掉箱子的差別是**玩家要走過去撿**
     —— Boss 死掉的當下場面最亂，把獎勵放在地上等於把決策延後到安全的那一刻，
     而「要不要為了那個箱子穿過敵群」本來就是這個專案一直在用的設計重點。

     金箱的規格在 `sim/chest.ts`：五選三、第一格保證是主／副／大招其中一個的
     強化、史詩與傳說權重加倍。所以「打贏 Boss 一定拿得到機體強化」沒有變，
     變的只是交付方式。 */
  dropChest(bx, bz, false, 'gold');
  /* 掉一筆大額經驗當獎勵。原本這裡會開補給台與傳送門，
     那兩個在 M7 退場了（GDD §15）。 */
  for (var i = 0; i < 12; i++) {
    var a = (i / 12) * TAU;
    dropOrb(bx + Math.cos(a) * 3.2, bz + Math.sin(a) * 3.2, 12);
  }
}

export function updateStageFlow(dt) {
  if (STATE.phase !== 'wave' && STATE.phase !== 'boss') return;
  STATE.t += dt;

  // 換幕
  var act = Math.min(3, Math.floor(STATE.t / ACT_LEN));
  if (act !== RS.act) enterAct(act);

  // Boss 定時降臨
  if (RS.nextBoss < BOSS_AT.length && STATE.t >= BOSS_AT[RS.nextBoss]) {
    startBoss(RS.nextBoss);
    RS.nextBoss++;
  }

  /* 精英：1:00 起每 60 秒一隻。**這才是「Boss 密度」的來源** ——
     VS 的 30 分鐘裡有 22 次 boss 生成，而那些「boss」就是雜兵的強化變體。
     四隻大型 Boss 只給段落感，密度靠這一層（`sim/elites.ts` 有完整說明）。 */
  if (eliteDue(STATE.t, BOSS_AT)) {
    /* M12 起是**成組**降臨（2–4 隻，跟著時間長）。放不出來（場上精英已達上限）
       就短暫推遲，不要吃掉整個週期 —— 否則玩家清完場還要再等 55 秒。 */
    var got = spawnEliteSquad(STATE.t);
    scheduleNextElite(STATE.t, got === 0);
  }

  /* ⚠️ **20:00 不再是勝利，是最終 Boss 降臨**（2026-08-06 使用者拍板）。
     通關條件搬到 `onBossKilled`（打倒 `FINAL_BOSS_ID`）。

     這裡刻意**不留「時間到就贏」的後路**：留了的話最終 Boss 就變成
     一個可以無視的裝飾（站著不打也會贏），而牠是整場的結尾考試。
     時鐘照走到 `RUN_LEN` 之後，難度曲線那一側用最後一段的值續著
     （`data/pacing.ts` 的查表對超出範圍的時間回最後一段）。 */

  /* 怪潮要排在生成器**之前** —— `updateHorde` 改的是 `HORDE.mul`，
     而生成器這一步就要用它。排在後面的話每一次切換都會晚一幀生效，
     退潮與壓入的邊界因此各差一幀（不影響正確性，但診斷會對不上）。 */
  updateHorde(dt);
  updateSpawner(dt);
  updateWaves(dt);
  showPrompt('');
}
